import React, { useEffect, useRef, useState } from "react";
import { FiArrowUp } from "react-icons/fi";
import { useOriVoice } from "apps/user/learner/hooks/use-ori-voice";
import { useTranslation } from "lib/i18n/use-translation";
import OriVoiceButton from "./OriVoiceButton";
import styles from "./OriComposer.module.scss";

interface OriComposerProps {
  onSend: (text: string, source?: "voice" | "keyboard") => void;
  isConnected: boolean;
  isSending: boolean;
  /** Fires when the draft goes empty to non-empty or back, and on unmount. */
  onDraftChange?: (hasText: boolean) => void;
}

const OriComposer: React.FC<OriComposerProps> = ({
  onSend,
  isConnected,
  isSending,
  onDraftChange,
}) => {
  const { t } = useTranslation(["ori"]);
  const [value, setValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const hasText = value.length > 0;
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  useEffect(() => {
    onDraftChangeRef.current?.(hasText);
  }, [hasText]);
  // The composer unmounts (past-day view, QA toggle) with its draft; the page
  // must not keep believing there is one.
  useEffect(() => () => onDraftChangeRef.current?.(false), []);

  const {
    isSupported: voiceSupported,
    state: voiceState,
    hasTranscript,
    error: voiceError,
    amplitude: voiceAmplitude,
    capturingVoice,
    startListening,
    cancel: cancelVoice,
    send: sendVoice,
  } = useOriVoice({ onSend: (text) => onSend(text, "voice") });

  const canSend = value.trim().length > 0 && isConnected && !isSending;
  const isListening = voiceState === "listening";

  const resize = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  const submit = () => {
    if (!canSend) return;
    onSend(value.trim(), "keyboard");
    setValue("");
    if (textareaRef.current) textareaRef.current.style.height = "";
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  const sendButton = (onClick: () => void, disabled: boolean) => (
    <button
      type="button"
      className={styles.sendButton}
      onClick={onClick}
      disabled={disabled}
      aria-label={t("ori:chat.composer.sendA11y")}
      data-testid="ori-composer-send"
    >
      <FiArrowUp className={styles.sendIcon} aria-hidden />
    </button>
  );

  const showMic =
    voiceSupported && value.trim() === "" && isConnected && !isSending;

  return (
    <>
      {voiceError && (
        <div
          className={styles.voiceError}
          role="alert"
          data-testid="ori-voice-error"
        >
          {voiceError}
        </div>
      )}
      <div className={styles.composer}>
        {isListening ? (
          <>
            <OriVoiceButton
              state={voiceState}
              onStart={startListening}
              onStop={cancelVoice}
              amplitude={voiceAmplitude}
              capturing={capturingVoice}
            />
            {sendButton(sendVoice, !hasTranscript)}
          </>
        ) : (
          <>
            <textarea
              ref={textareaRef}
              className={styles.textarea}
              value={value}
              onChange={(event) => {
                setValue(event.target.value);
                resize(event.target);
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("ori:chat.input.placeholder")}
              rows={1}
              disabled={!isConnected}
              data-testid="ori-composer-input"
            />
            {showMic ? (
              <OriVoiceButton
                state={voiceState}
                onStart={startListening}
                onStop={cancelVoice}
              />
            ) : (
              sendButton(submit, !canSend)
            )}
          </>
        )}
      </div>
    </>
  );
};

export default OriComposer;
